import { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Loader2, Trophy, TrendingUp, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Results() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  
  // Check if user has premium/subscriber access (admin email or active subscription)
  const hasSubscriberAccess = isAuthenticated && (
    user?.email === 'admin@primestatlab.com' || 
    user?.subscription_status === 'active'
  );

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const [summaryRes, historyRes] = await Promise.all([
          axios.get(`${API}/results/summary`),
          axios.get(`${API}/results/history`)
        ]);
        setSummary(summaryRes.data);
        setHistory(historyRes.data);
      } catch (error) {
        console.error('Failed to fetch results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getOutcomeColor = (outcome) => {
    if (outcome === 'win') return 'text-win';
    if (outcome === 'loss') return 'text-loss';
    return 'text-push';
  };

  const getOutcomeBg = (outcome) => {
    if (outcome === 'win') return 'bg-win/20 border-win';
    if (outcome === 'loss') return 'bg-loss/20 border-loss';
    return 'bg-push/20 border-push';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Pick Results & Win Rate Tracker | PrimeStatLab</title>
        <meta name="description" content="View our transparent, verified pick history. Track our NBA player prop win rate and see our complete betting record. Full accountability on every pick." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Results Tracker - PrimeStatLab Win Rate" />
        <meta property="og:description" content="Transparent, verified pick history. See our complete betting record." />
        <meta property="og:url" content="https://primestatlab.com/results" />
        <meta property="og:site_name" content="PrimeStatLab" />
        <meta name="twitter:card" content="summary" />
        <link rel="canonical" href="https://primestatlab.com/results" />
      </Helmet>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="results-page">
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">RESULTS TRACKER</h1>
          <p className="text-lg text-muted-foreground">Transparent, verified pick history</p>
        </div>

        {/* Record Summary - Subscribers Only */}
        {hasSubscriberAccess ? (
          summary && summary.total_picks > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-card border border-border p-8 text-center" data-testid="summary-win-rate">
                <Trophy className="text-primary mx-auto mb-4" size={32} />
                <div className="font-mono text-4xl font-bold text-primary mb-2">{summary.win_percentage}%</div>
                <div className="text-sm text-muted-foreground uppercase tracking-widest">Win Rate</div>
              </div>
              <div className="bg-card border border-border p-8 text-center" data-testid="summary-wins">
                <div className="font-mono text-4xl font-bold text-accent mb-2">{summary.wins}</div>
                <div className="text-sm text-muted-foreground uppercase tracking-widest">Wins</div>
              </div>
              <div className="bg-card border border-border p-8 text-center" data-testid="summary-losses">
                <div className="font-mono text-4xl font-bold text-loss mb-2">{summary.losses}</div>
                <div className="text-sm text-muted-foreground uppercase tracking-widest">Losses</div>
              </div>
              <div className="bg-card border border-border p-8 text-center" data-testid="summary-total">
                <TrendingUp className="text-secondary mx-auto mb-4" size={32} />
                <div className="font-mono text-4xl font-bold mb-2">{summary.total_picks}</div>
                <div className="text-sm text-muted-foreground uppercase tracking-widest">Total Picks</div>
              </div>
            </div>
          )
        ) : (
          <div className="bg-card border border-border p-8 mb-12 text-center" data-testid="subscribe-prompt">
            <Lock className="text-muted-foreground mx-auto mb-4" size={48} />
            <h3 className="font-heading text-2xl font-bold mb-2">RECORD HIDDEN</h3>
            <p className="text-muted-foreground mb-6">Subscribe to view our full win/loss record and detailed statistics</p>
            <Link to="/subscribe">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono uppercase tracking-wider">
                Subscribe to Unlock
              </Button>
            </Link>
          </div>
        )}

        {/* Pick History - Subscribers Only */}
        {hasSubscriberAccess ? (
          <div className="bg-card border border-border">
            <div className="p-6 border-b border-border">
              <h2 className="font-heading text-2xl font-bold">PICK HISTORY</h2>
            </div>
            {history.length === 0 ? (
              <div className="p-12 text-center" data-testid="no-history">
                <p className="text-muted-foreground">No results tracked yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="results-table">
                  <thead className="bg-surface-highlight">
                    <tr className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">Sport</th>
                      <th className="text-left p-4">Player</th>
                      <th className="text-left p-4">Prop</th>
                      <th className="text-left p-4">Line</th>
                      <th className="text-left p-4">Pick</th>
                      <th className="text-left p-4">Result</th>
                      <th className="text-left p-4">Outcome</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-sm">
                    {history.map((pick, index) => (
                      <tr
                        key={pick.id}
                        className={`border-t border-border hover:bg-surface-highlight transition-colors ${
                          index % 2 === 0 ? 'bg-white/5' : ''
                        }`}
                        data-testid={`result-row-${pick.id}`}
                      >
                        <td className="p-4 text-muted-foreground">{formatDate(pick.pick_date)}</td>
                        <td className="p-4">{pick.sport}</td>
                        <td className="p-4 font-bold">{pick.player_name}</td>
                        <td className="p-4">{pick.prop_type}</td>
                        <td className="p-4">{pick.line}</td>
                        <td className="p-4 text-primary font-bold">{pick.pick}</td>
                        <td className="p-4">{pick.result || '-'}</td>
                        <td className="p-4">
                          <span className={`px-3 py-1 border rounded-sm uppercase text-xs font-bold ${
                            getOutcomeBg(pick.outcome)
                          } ${getOutcomeColor(pick.outcome)}`}>
                            {pick.outcome}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-card border border-border p-12 text-center">
            <h2 className="font-heading text-2xl font-bold mb-2">PICK HISTORY</h2>
            <p className="text-muted-foreground">Subscribe to view detailed pick history and results.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}